import { useState, useEffect } from 'react';
import { cardState, splitDisplayName, STATE_LABEL, AGREEMENT_LABEL } from './identityAgreement.js';
import BrandModel from './BrandModel.jsx';

// The per-item identity card of the shadow phase (Prism spec kg_quote_path §8,
// ruling 8): the worker's answer beside the KG's, each as a labelled brand and
// model, one agreement badge, the tier and confidence that produced the KG
// answer, its evidence, and the operator's verdict buttons. The worker keeps
// authority; the verdict is a label for BOTH systems (a pinned attachment in
// the graph). The page decides what a verdict does to brand/model and prices
// (ruling 9). Only the actions that apply are shown: Confirm and Reject need a
// KG answer to judge; picking from the catalogue or creating a product is
// always open, and is the way to teach the KG an item it could not identify.
// A recorded verdict can be changed: verdicts are append-only in the graph and
// the latest one wins, so "Change…" reopens the actions and the next verdict
// supersedes the last. With `onOpenProduct` the KG answer is a button that
// opens the catalogue product (the page passes it for admins only, since the
// catalogue review API is admin-only).

function hostOf(url) {
    try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
}

function Evidence({ kg }) {
    if (!kg?.evidence) return null;
    const { alias_key, free_name, citations = [] } = kg.evidence;
    if (!alias_key && !free_name && citations.length === 0) return null;
    return (
        <div className="qa-idc-evidence">
            {alias_key && <span title="The stored name that matched exactly">alias: <code>{alias_key}</code></span>}
            {free_name && <span title="What the lookup named before choosing a candidate">named: {free_name}</span>}
            {citations.slice(0, 3).map(url => (
                <a key={url} href={url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>{hostOf(url)}</a>
            ))}
        </div>
    );
}

// The KG's product: thumbnail and the labelled pair. A button when the page
// can open the catalogue product, a plain block otherwise.
function KgAnswer({ kg, split, onOpen }) {
    const body = (
        <>
            {kg.thumbnail_url && <img className="qa-idc-thumb" src={kg.thumbnail_url} alt="" />}
            <BrandModel brand={split.brand} model={split.model} category={kg.category} />
        </>
    );
    if (!onOpen) return <div className="qa-idc-answer">{body}</div>;
    return (
        <button type="button" className="qa-idc-answer qa-idc-open"
            aria-label={`Open ${kg.display_name} in the catalogue`} title="Open in the catalogue"
            onClick={() => onOpen(kg.product_model_id)}>
            {body}
        </button>
    );
}

export default function IdentityCard({ card, item, onVerdict, disabled = false, onOpenProduct = null }) {
    const [newProduct, setNewProduct] = useState(null); // { brand_name, model } while typing a new product
    const state = cardState(card);
    const kg = card?.kg ?? null;
    const kgProduct = kg?.product_model_id
        ? { product_model_id: kg.product_model_id, display_name: kg.display_name, brand: kg.brand }
        : null;
    const kgSplit = kgProduct ? splitDisplayName(kgProduct.display_name, kgProduct.brand) : null;
    const agreement = card?.agreement ?? 'unknown';
    const [changing, setChanging] = useState(false); // the operator reopened a recorded verdict
    useEffect(() => { setChanging(false); }, [card?.verdict?.recorded_at]); // a new verdict landed: close it
    const showActions = !card?.verdict || changing;
    const canAct = !disabled;

    function stop(e) { e.stopPropagation(); }

    return (
        <div className={`qa-idc qa-idc-${state}`} onClick={stop} data-testid="identity-card">
            <div className="qa-idc-row">
                <div className="qa-idc-col">
                    <div className="qa-idc-k">Worker</div>
                    <BrandModel brand={item?.brand} model={item?.model} />
                </div>
                <div className="qa-idc-col">
                    <div className="qa-idc-k">
                        KG
                        {kg?.tier != null && <span className="qa-idc-tier" title="Tier 0 = seen before (alias). Tier 3 = web-search lookup.">T{kg.tier}</span>}
                        {typeof kg?.confidence === 'number' && <span className="qa-idc-conf">{Math.round(kg.confidence * 100)}%</span>}
                    </div>
                    {kgProduct ? (
                        <KgAnswer kg={kg} split={kgSplit} onOpen={onOpenProduct} />
                    ) : (
                        <div className="qa-idc-v qa-muted"
                            title={state === 'lookup_queued' ? 'The photo lookup has not run yet. The daily lookup cap or the queue backlog holds it; it runs one item at a time.' : undefined}>
                            {STATE_LABEL[state]}
                        </div>
                    )}
                    <Evidence kg={kg} />
                </div>
                <div className="qa-idc-col qa-idc-state">
                    {agreement !== 'unknown' && (
                        <span className={`qa-idc-badge qa-idc-badge-${agreement}`}>{AGREEMENT_LABEL[agreement]}</span>
                    )}
                    {kgProduct && <span className="qa-idc-statelabel">{STATE_LABEL[state]}</span>}
                </div>
            </div>

            {card?.verdict && (
                <div className="qa-idc-verdict">
                    Verdict: <b>{card.verdict.kind}</b>
                    {card.verdict.display_name ? <> → {card.verdict.display_name}</> : null}
                    <span className="qa-muted"> by {card.verdict.actor}</span>
                    {!changing && (
                        <button type="button" className="btn btn-sm qa-idc-change" disabled={disabled}
                            title="Record a different verdict; the latest one counts"
                            onClick={() => setChanging(true)}>
                            Change…
                        </button>
                    )}
                </div>
            )}
            {showActions && (
                <div className="qa-idc-actions">
                    {kgProduct && (
                        <button type="button" className="btn btn-sm btn-primary" disabled={!canAct}
                            title="The KG answer is right"
                            onClick={() => onVerdict(item, card, { kind: 'confirm', product: kgProduct })}>
                            Confirm
                        </button>
                    )}
                    {kgProduct && (
                        <button type="button" className="btn btn-sm btn-danger" disabled={!canAct}
                            title="The KG answer is wrong and you do not know the product"
                            onClick={() => onVerdict(item, card, { kind: 'reject', rejectedProductId: kgProduct.product_model_id })}>
                            Reject
                        </button>
                    )}
                    <button type="button" className="btn btn-sm" disabled={!canAct}
                        title="Pick the right product from the catalogue; records the verdict and teaches the KG this item's text"
                        onClick={() => onVerdict(item, card, { kind: 'correct' })}>
                        {kgProduct ? 'Pick another…' : 'Pick from catalogue…'}
                    </button>
                    <button type="button" className="btn btn-sm" disabled={!canAct}
                        title="The product is not in the catalogue yet: type its brand and model to create it and record the verdict"
                        onClick={() => setNewProduct(newProduct ? null : { brand_name: kg?.brand ?? item?.brand ?? '', model: item?.model ?? '' })}>
                        Create product…
                    </button>
                </div>
            )}

            {newProduct && showActions && (
                <div className="qa-idc-newproduct">
                    <input className="description-input" placeholder="Brand" value={newProduct.brand_name}
                        onChange={e => setNewProduct({ ...newProduct, brand_name: e.target.value })} />
                    <input className="description-input" placeholder="Model" value={newProduct.model}
                        onChange={e => setNewProduct({ ...newProduct, model: e.target.value })} />
                    <span className="qa-muted">
                        Creates <b>{newProduct.brand_name.trim() || '?'}</b> / <b>{newProduct.model.trim() || '?'}</b>
                        {' '}as a new confirmed product (and the brand, if it does not exist) and records the verdict.
                    </span>
                    <button type="button" className="btn btn-sm btn-primary"
                        disabled={!newProduct.brand_name.trim() || !newProduct.model.trim()}
                        onClick={() => {
                            const np = { brand_name: newProduct.brand_name.trim(), model: newProduct.model.trim() };
                            setNewProduct(null);
                            onVerdict(item, card, { kind: 'correct', newProduct: np, product: { display_name: `${np.brand_name} ${np.model}`, brand: np.brand_name } });
                        }}>
                        Create and confirm
                    </button>
                </div>
            )}
        </div>
    );
}
